import './table-retailer.scss';
import React, {useEffect, useState} from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {useTranslation} from 'react-i18next';
import apiClient from '../../../../utils/apiClient';
import {useNavigate} from 'react-router-dom';
import Grid from '@mui/material/Grid2';
import decodeToken from '../../../../utils/decode-token';
import {Alert, Snackbar} from '@mui/material';
import { MARKETS } from '../../../../constants/constants';
import SortableHeaderCell from '../../common/table/sortable-header-cell';
import RowActionsMenu from '../../common/table/row-actions-menu';
import TableResultsSummary from '../../common/table/table-results-summary';
import handleEmptyTableResponse from '../../common/table/utils/handle-empty-table-response';
import {
  compareText,
  countryLabelOptions,
  getNextSortDirection,
  getStoredFilters,
  includesNormalized,
  pluckUniqueTruthyValues,
  saveFilters,
  sortRows,
  uniqueTruthyValues,
} from '../../../../utils/tableUtils';

const FILTERS_STORAGE_KEY = 'admin-retailer-filters';
const INITIAL_FILTERS = {
  name: '',
  market: '',
  country: '',
  city: '',
};
const SORT_FIELDS = {
  NAME: 'name',
};
const SORT_COMPARATORS = {
  [SORT_FIELDS.NAME]: (a, b) => compareText(a.name, b.name),
};

function TableRetailer({reload, onDataLoaded, showSummary = true, onSummaryChange = () => {}}) {
  const {t} = useTranslation();
  const token = sessionStorage.getItem('token') || null;
  const role = decodeToken(token).role;
  const [dataLoaded, setDataLoaded] = useState(false);
  const loggedUser = JSON.parse(sessionStorage.getItem('loggedUser') || null);
  const navigate = useNavigate();

  const [retailers, setRetailers] = useState([]);
  const [filteredRetailers, setFilteredRetailers] = useState([]);
  const [initialMarkets] = useState(MARKETS);

  const markets = initialMarkets.filter(market => loggedUser?.market.includes(market.name));

  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Estado de orden
  const [order, setOrder] = useState('');
  const [orderBy, setOrderBy] = useState('');
  const handleRequestSort = (property) => {
    setOrder(getNextSortDirection(orderBy, order, property));
    setOrderBy(property);
  };

  const sortData = (array) => sortRows(array, orderBy, order, SORT_COMPARATORS);


  const fetchData = async () => {
    const authToken = token;
    const url = `${process.env.REACT_APP_API_URL}/admin/retailers`;

    const config = {
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Cache-Control': 'no-cache',
      },
    };

    try {
      const response = await apiClient.get(url, config);
      if (response.status === 200) {
        const data = response.data;
        const initialFilters = getStoredFilters(FILTERS_STORAGE_KEY, INITIAL_FILTERS);
        setFilters(initialFilters);
        setRetailers(data);
        applyFilters(initialFilters, data);
        setDataLoaded(true);
        onDataLoaded();

        const market = markets.find((m) => m.name === initialFilters.market);
        const availableCountries = market ? market.countries : pluckUniqueTruthyValues(data, 'area_code');
        const uniqueCities = pluckUniqueTruthyValues(data, 'city');
        setCountries(orderCountries(availableCountries));
        setCities(uniqueCities);
      } else if (response.status === 204) {
        handleEmptyTableResponse({
          clearStateSetters: [setRetailers, setFilteredRetailers, setCountries, setCities],
          onDataLoaded,
          onSummaryChange,
          setDataLoaded,
        });
      }
    } catch (error) {
      if (error.response) {
        setSnackbarOpen(true);
        setSnackbarMessage(t('common-internal-server-error'));
      }
    }
  };

  useEffect(() => {
    if (reload) {
      fetchData();
    }
    // eslint-disable-next-line
  }, [reload, token, onDataLoaded, t]);

  const handleFilterChange = (e) => {
    const {name, value} = e.target;

    if (name === "market") {
      if (value === "") {
        const allCountries = pluckUniqueTruthyValues(retailers, 'area_code');
        setCountries(orderCountries(allCountries));

        const allCities = pluckUniqueTruthyValues(retailers, 'city');

        setCities(allCities);

        setFilters({
          ...filters,
          market: '',
          country: '',
          city: ''
        });
      } else {
        const selectedMarket = markets.find((market) => market.name === value);
        const updatedCountries = selectedMarket ? selectedMarket.countries : pluckUniqueTruthyValues(retailers, 'area_code');

        setCountries(orderCountries(updatedCountries));

        const autoSelectedCountry = updatedCountries.length === 1 ? updatedCountries[0] : "";

        const filteredCities = retailers
          .filter((retailer) => updatedCountries.includes(retailer.area_code))
          .map((retailer) => retailer.city);
        const uniqueCities = uniqueTruthyValues(filteredCities);

        setCities(uniqueCities);

        setFilters({
          ...filters,
          market: value,
          country: autoSelectedCountry,
          city: '', // Reset city
        });
      }

    } else if (name === "country") {
      if (value === "") {
        const selectedMarket = markets.find((market) => market.name === filters.market);

        const filteredCities = selectedMarket
          ? retailers
            .filter((retailer) => selectedMarket.countries.includes(retailer.area_code))
            .map((retailer) => retailer.city)
          : [];

        setCities(uniqueTruthyValues(filteredCities));

        setFilters({
          ...filters,
          country: "",
          city: "",
        });
      } else {
        const filteredCities = retailers
          .filter((retailer) => retailer.area_code === value)
          .map((retailer) => retailer.city);

        setCities(uniqueTruthyValues(filteredCities));

        const selectedMarket = markets.find((market) => market.countries.includes(value));

        setFilters({
          ...filters,
          country: value,
          market: selectedMarket ? selectedMarket.name : filters.market, // Auto-selecciona el market si hay uno solo
          city: "",
        });
      }
    } else if (name === "city") {
      const retailerWithCity = retailers.find((retailer) => retailer.city === value);

      if (retailerWithCity) {
        const relatedCountry = retailerWithCity.area_code;
        const relatedMarket = markets.find((market) => market.countries.includes(relatedCountry))?.name || filters.market;

        setFilters({
          ...filters,
          city: value,
          country: relatedCountry,
          market: relatedMarket,
        });
      } else {
        setFilters({
          ...filters,
          city: value,
        });
      }
    } else {
      setFilters({
        ...filters,
        [name]: value,
      });
    }
  };


  const applyFilters = (appliedFilters = filters, retailersData = retailers) => {
    let filtered = [...retailersData];
    if (appliedFilters.name) {
      filtered = filtered.filter((retailer) =>
        includesNormalized(retailer.name, appliedFilters.name)
      );
    }
    if (appliedFilters.market) {
      const selectedMarket = markets.find(
        (market) => market.name === appliedFilters.market
      );
      if (selectedMarket) {
        filtered = filtered.filter((retailer) =>
          selectedMarket.countries.includes(retailer.area_code)
        );
      }
    }
    if (appliedFilters.country) {
      filtered = filtered.filter(
        (retailer) => retailer.area_code === appliedFilters.country
      );
    }
    if (appliedFilters.city) {
      filtered = filtered.filter((retailer) =>
        retailer.city.toLowerCase().includes(appliedFilters.city.toLowerCase())
      );
    }
    setFilteredRetailers(filtered);
    onSummaryChange({ total: filtered.length });
    saveFilters(FILTERS_STORAGE_KEY, appliedFilters);
  };

  const handleApplyFilter = () => {
    applyFilters();
  };

  const resetFilters = () => {
    const initialFilters = INITIAL_FILTERS;
    const allCountries = pluckUniqueTruthyValues(retailers, 'area_code');
    const allCities = pluckUniqueTruthyValues(retailers, 'city');

    setCountries(orderCountries(allCountries));
    setCities(allCities);

    setFilters(initialFilters);
    saveFilters(FILTERS_STORAGE_KEY, initialFilters);

    applyFilters(initialFilters, retailers);
  };

  const orderCountries = (countries) => {
    return countryLabelOptions(countries, t);
  };

  return (
    <>
      {dataLoaded ? (
        retailers && retailers.length !== 0 ? (
          <>
            <div className="filters">
              <p>{t('common-search')}</p>
              <Grid container spacing={3} rowSpacing={0}>
                <Grid size={{xs: 12, sm: 6, md: 3}}>
                  <div className="form-control">
                    <label htmlFor="name">{t('common-name')}</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={filters.name}
                      onChange={handleFilterChange}
                    />
                  </div>
                </Grid>
                {(role === 'admin' || role === 'marketAdmin') && markets.length > 1 && (
                  <Grid size={{xs: 12, sm: 6, md: 2}}>
                    <div className="form-control">
                      <label htmlFor="market">{t('common-market')}</label>
                      <select
                        name="market"
                        id="market"
                        className="normal"
                        value={filters.market}
                        onChange={handleFilterChange}
                      >
                        <option value="">{t('common-all-market')}</option>
                        {markets.map((market) => (
                          <option key={market.name} value={market.name}>
                            {t(`market-${market.name}`)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </Grid>
                )}
                {(role === 'admin' ||
                  role === 'marketAdmin' ||
                  role === 'countryAdmin') && (markets.length > 1 || countries.length > 1) && (
                  <Grid size={{xs: 12, sm: 6, md: 2}}>
                    <div className="form-control">
                      <label htmlFor="country">{t('common-country')}</label>
                      <select
                        name="country"
                        id="country"
                        className="normal"
                        value={filters.country}
                        onChange={handleFilterChange}
                      >
                        <option value="">{t('common-all-country')}</option>
                        {countries.map((country) => (
                          <option key={country.value} value={country.value}>
                            {country.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </Grid>
                )}
                <Grid size={{xs: 12, sm: 6, md: 2}}>
                  <div className="form-control">
                    <label htmlFor="city">{t('common-city')}</label>
                    <select
                      name="city"
                      id="city"
                      className="normal"
                      value={filters.city}
                      onChange={handleFilterChange}
                    >
                      <option value="">{t('common-all-city')}</option>
                      {cities.map((city) => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </select>
                  </div>
                </Grid>
              </Grid>
              {showSummary && (
                <TableResultsSummary count={filteredRetailers.length} />
              )}

              <Grid container spacing={3}>
                <Grid size={{xs: 12, sm: 6, md: 2}}>
                  <div className="actions">
                    <button className="btn" onClick={handleApplyFilter}>
                      {t('common-apply')}
                    </button>
                  </div>
                </Grid>
                <Grid size={{xs: 12, sm: 6, md: 3}}>
                  <div className="actions">
                    <button onClick={resetFilters} className="btn text narrow">{t('common-reset-filters')}</button>
                  </div>
                </Grid>
              </Grid>
            </div>

            <div className="table-wrapper">
              {filteredRetailers.length !== 0 ? (
                <Table
                  sx={{minWidth: 650}}
                  aria-label="simple table"
                  className="user-table admin-retailer"
                >
                  <TableHead>
                    <TableRow>
                      <SortableHeaderCell
                        field={SORT_FIELDS.NAME}
                        orderBy={orderBy}
                        order={order}
                        onRequestSort={handleRequestSort}
                        label={t('common-name')}
                      />
                      <TableCell align="left"><span className="text">{t('common-country')}</span></TableCell>
                      <TableCell align="left"><span className="text">{t('common-city')}</span></TableCell>
                      <TableCell align="left"><span className="text">{t('common-number-sale-people')}</span></TableCell>
                      <TableCell align="right"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {
                      sortData(filteredRetailers).map((row) => (
                        <TableRow key={row.id}>
                          <TableCell align="left"><span className="text">{row.name}</span></TableCell>
                          <TableCell align="left"><span
                            className="text">{t(`countries-${row.area_code}`)}</span></TableCell>
                          <TableCell align="left"><span className="text">{row.city}</span></TableCell>
                          <TableCell align="left"><span className="text">{row.num_salespersons}</span></TableCell>
                          <TableCell align="right">
                            <RowActionsMenu
                              actions={[
                                {
                                  key: 'retailer-info',
                                  label: t('common-retailer'),
                                  onClick: () => navigate(`/admin/retailer-info/${row.id}`),
                                },
                              ]}
                            />
                          </TableCell>
                          {/*<TableCell align="right">
                            <Link
                              to={`/admin/retailer-info/${row.id}`}
                              className="profile"
                            >
                              <span className="icon-chevron-right"></span>
                              <span className="link">
                              {t('common-retailer-details')}
                            </span>
                            </Link>
                          </TableCell>*/}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="no-result">
                  <h4 className="txt-center">{t('admin-main-title-empty')}</h4>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="no-result">
            <h2 className="txt-center">
              {t('admin-main-tab3-title-empty')}
              <small>{t('admin-retailer-tab3-text-empty')}</small>
            </h2>
          </div>
        )
      ) : (
        <div>{t('common-loading')}</div>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
      >
        <Alert severity="error" sx={{width: '100%'}}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default TableRetailer;
